import React, { useState, useCallback } from 'react';
import { Trophy } from 'lucide-react';
import WizardShell from '../wizards/WizardShell';
import WizardSuccess from '../wizards/WizardSuccess';
import NameStep from '../wizards/steps/NameStep';
import TournamentStep, { tournamentName } from '../wizards/steps/TournamentStep';
import ReviewRow from '../wizards/steps/ReviewRow';
import InviteStep from '../wizards/steps/InviteStep';
import GafferStep from '../wizards/steps/GafferStep';
import ManagerPowersStep from '../wizards/steps/ManagerPowersStep';
import * as miniLeaguesApi from '../../api/miniLeagues';

/**
 * Creates a Mini League and registers its first tournament.
 *
 * These are two separate API calls: the league (and its join code) is created
 * first, then the tournament, which spawns the league's first game. If the
 * second call fails the league still exists and is perfectly valid, so the
 * created league is held in state and reused on retry — otherwise a retry
 * would create a duplicate league every time.
 */
function CreateMiniLeagueWizard({ tournaments, onClose, onCreated }) {
  const [name, setName] = useState('');
  const [selection, setSelection] = useState({ tournamentId: '', startingMatchWeek: '' });
  const [gafferEnabled, setGafferEnabled] = useState(false);
  const [inviteeIds, setInviteeIds] = useState([]);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [createdLeague, setCreatedLeague] = useState(null);
  const [done, setDone] = useState(false);

  const handleSelectionChange = useCallback((next) => setSelection(next), []);

  const chosenTournament = tournaments.find(
    (t) => String(t.id) === String(selection.tournamentId)
  );

  const handleComplete = async () => {
    setError('');
    setSubmitting(true);
    let league = createdLeague;
    try {
      if (!league) {
        league = await miniLeaguesApi.createLeague({ name, gafferEnabled, inviteePlayerIds: inviteeIds });
        setCreatedLeague(league);
      }
      await miniLeaguesApi.addTournament(league.id, {
        tournamentId: parseInt(selection.tournamentId),
        startingMatchWeek: parseInt(selection.startingMatchWeek),
      });
      setDone(true);
    } catch (err) {
      const reason = err.name === 'ApiError'
        ? (err.body?.error || 'Something went wrong')
        : 'Something went wrong';
      if (league) {
        // The league survived; only the tournament failed.
        setError(`Your league was created (code ${league.leagueCode}), but the tournament could not be added: ${reason}. Try a different match week.`);
      } else {
        setError(`Failed to create league: ${reason}`);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (done && createdLeague) {
    return (
      <WizardSuccess
        title="Mini League Created"
        icon={Trophy}
        heading={`${createdLeague.name} is live`}
        blurb={'Share this code with your mates \u2014 it gets them into every tournament in '
          + 'the league, including ones you add later.'}
        code={createdLeague.leagueCode}
        codeLabel="Copy league code"
        shareKind="league"
        shareName={createdLeague.name}
        footnote={`First game: ${tournamentName(chosenTournament)}, starting week ${selection.startingMatchWeek}.`}
        onDone={onCreated}
      />
    );
  }

  const steps = [
    {
      id: 'name',
      label: 'Name',
      isComplete: () => name.trim().length > 0,
      render: () => (
        <NameStep
          label="League Name"
          placeholder="e.g., The Office Mini-League"
          hint="Your league runs season after season — pick something that lasts"
          value={name}
          onChange={setName}
        />
      ),
    },
    {
      id: 'tournament',
      label: 'Tournament',
      isComplete: () => Boolean(selection.tournamentId && selection.startingMatchWeek),
      render: () => (
        <>
          <p className="helper-text wizard-intro">
            Pick the competition your league starts with. You can add more
            tournaments to the league at any time.
          </p>
          <TournamentStep
            tournaments={tournaments}
            value={selection}
            onChange={handleSelectionChange}
          />
        </>
      ),
    },
    {
      id: 'gaffer',
      label: 'The Gaffer',
      isComplete: () => true,
      render: () => (
        <GafferStep
          checked={gafferEnabled}
          onChange={setGafferEnabled}
          intro="This applies to every game the league starts, and you can change it later from the league page."
        />
      ),
    },
    {
      id: 'invite',
      label: 'Invite',
      isComplete: () => true,
      render: () => (
        <InviteStep
          selectedIds={inviteeIds}
          onToggle={(id) =>
            setInviteeIds((prev) =>
              prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
            )
          }
        />
      ),
    },
    {
      id: 'powers',
      label: 'Manager',
      isComplete: () => true,
      render: () => <ManagerPowersStep kind="league" />,
    },
    {
      id: 'review',
      label: 'Review',
      isComplete: () => true,
      render: () => (
        <div className="review-panel">
          <ReviewRow label="League name" value={name} />
          <ReviewRow label="First tournament" value={tournamentName(chosenTournament)} />
          <ReviewRow label="Starts" value={`Week ${selection.startingMatchWeek}`} />
          <ReviewRow label="The Gaffer" value={gafferEnabled ? 'Playing' : 'Not playing'} />
          <ReviewRow label="Run by" value="You" />
          <p className="helper-text">
            You'll get a league code to share. When a game finishes, the next one
            starts automatically for the rest of the season.
          </p>
        </div>
      ),
    },
  ];

  return (
    <WizardShell
      title="New Mini League"
      steps={steps}
      submitLabel={createdLeague ? 'Retry Tournament' : 'Create League'}
      busyLabel="Creating..."
      error={error}
      submitting={submitting}
      onComplete={handleComplete}
      onClose={onClose}
    />
  );
}

export default CreateMiniLeagueWizard;
